import { Button } from '@radix-ui/themes';
import PropTypes from 'prop-types';

/**
 * Banner component for issue actions (submit, validate, reject)
 * @param {string} props.title - Title text displayed on the left
 * @param {React.ReactNode} props.icon - Optional icon displayed before the title
 * @param {string} props.buttonText - Text displayed on the button (single button mode)
 * @param {React.ReactNode} props.buttonIcon - Optional icon displayed in the button (single button mode)
 * @param {Function} props.onClick - Function called when button is clicked (single button mode)
 * @param {boolean} props.disabled - Whether the button is disabled (single button mode)
 * @param {string} props.buttonClassName - Additional CSS classes for the button (single button mode)
 * @param {Array} props.buttons - Array of button configurations for multiple buttons mode
 * @param {boolean} props.isInWebView - Whether the app is in a web view (affects positioning)
 */
const IssueActionBanner = ({
  title,
  icon,
  buttonText,
  buttonIcon,
  onClick,
  disabled = false,
  buttonClassName = '',
  buttons = null,
  isInWebView = false
}) => {
  return (
    <div className={`${!isInWebView ? 'fixed' : 'sticky'} ${!isInWebView ? 'bottom-[75px]' : 'bottom-0'} z-40 w-full left-0 right-0 border-t-2 border-gray-200 bg-white/95 backdrop-blur-sm supports-[backdrop-filter]:bg-white/80 shadow-lg`}>
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            {icon && icon}
            <span className="font-medium">{title}</span>
          </div>
          {buttons ? (
            <div className="flex items-center gap-3">
              {buttons.map((button, index) => (
                <Button
                  key={index}
                  size="3"
                  disabled={button.disabled}
                  onClick={button.onClick}
                  className={`!font-semibold ${
                    button.disabled
                      ? '!bg-gray-300 !text-gray-500 !cursor-not-allowed'
                      : button.variant === 'secondary'
                      ? '!bg-white !text-black !border-2 !border-black hover:!bg-gray-100'
                      : '!bg-black !text-white hover:!bg-gray-800'
                  } ${button.className || ''}`}
                >
                  {button.icon && button.icon}
                  {button.text}
                </Button>
              ))}
            </div>
          ) : (
            <Button
              size="3"
              disabled={disabled}
              onClick={onClick}
              className={`!font-semibold ${
                disabled
                  ? '!bg-gray-300 !text-gray-500 !cursor-not-allowed'
                  : '!bg-black !text-white hover:!bg-gray-800'
              } ${buttonClassName}`}
            >
              {buttonIcon && buttonIcon}
              {buttonText}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

IssueActionBanner.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node,
  buttonText: PropTypes.string,
  buttonIcon: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  buttonClassName: PropTypes.string,
  buttons: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string.isRequired,
    icon: PropTypes.node,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    variant: PropTypes.oneOf(['primary', 'secondary']),
    className: PropTypes.string
  })),
  isInWebView: PropTypes.bool
};

export { IssueActionBanner };

